import type { CompareImpact, MetricDelta, RowComparison } from "./types";

/** Leading `YYYY-MM-DD` of an ISO date or timestamp. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}/;

/**
 * The earliest date the row itself says the contract already existed, or null
 * when nothing on it does.
 *
 * The earliest rather than any single field: this feeds a fail-closed check, so
 * a contract is only treated as new when *every* date the row carries agrees it
 * is. A contract signed months before its term opens must not read as new just
 * because the later of the two dates falls inside the period.
 */
export const earliestKnownStart = (
  ...dates: (string | null | undefined)[]
): string | null =>
  dates
    .map((date) => date?.match(DATE_ONLY)?.[0])
    .filter((date): date is string => !!date)
    .sort()[0] ?? null;

/**
 * Whether the contract is known to have begun inside the comparison window.
 *
 * Bounded at both ends. The lower bound is the period start; the upper bound
 * matters just as much, because the window closes at the present — the compare
 * endpoint always measures against live values — and a contract whose term
 * opens next quarter has not begun inside it. Such a contract can still carry
 * a positive figure, so without the upper bound it reads as new business that
 * has not happened yet.
 *
 * Compared as the calendar dates each side was written as, not as instants:
 * the period start is a UTC midnight and a contract date is a plain day, so
 * parsing either into a `Date` would let the viewer's offset decide a same-day
 * comparison. False whenever any of the three dates is missing or unparseable
 * — the caller uses this as positive evidence, so absence of an answer must
 * never read as one.
 */
export const startedWithinPeriod = (
  rowStart: string | null | undefined,
  periodStart: string | null | undefined,
  periodEnd: string | null | undefined,
  // Services opt out of the upper bound: the design labels an Upcoming service
  // New, so a term that opens after today still counts as new business there.
  allowFutureStart: boolean = false
): boolean => {
  const start = rowStart?.match(DATE_ONLY)?.[0];
  const opened = periodStart?.match(DATE_ONLY)?.[0];
  const closed = periodEnd?.match(DATE_ONLY)?.[0];
  if (!start || !opened || (!closed && !allowFutureStart)) return false;
  if (start < opened) return false;
  return allowFutureStart || start <= (closed as string);
};

/** The comparison window a metric's impact is resolved against. */
export interface ComparisonWindow {
  /** The earliest date the row says this grain's entity existed. */
  rowStart: string | null;
  /** The period start the comparison resolved. */
  periodStart: string | null;
  /**
   * The day the window closes. The endpoint compares against live values, so
   * this is the present rather than anything the response carries.
   */
  periodEnd: string | null;
  /** Grains whose design labels a not-yet-started row New set this. */
  allowFutureStart?: boolean;
}

/**
 * The impact to render for one metric.
 *
 * The server's label is authoritative except for one case it cannot express:
 * the contracts endpoint withholds the New label, so a contract with no prior
 * figure comes back `noChange` on every metric, which is indistinguishable
 * from one that genuinely did not move.
 *
 * `existed === false` alone is *not* that case. The endpoint documents it as
 * "no snapshot at or before the period start" — a fact about the snapshot
 * table, not about the contract. Financial snapshots only began recording when
 * that table shipped, so on any period reaching back past that point every
 * contract answers `existed: false`, and deriving New from it alone labels an
 * account's entire book as new business. New therefore also requires positive
 * evidence from the row that the contract itself began inside the window;
 * without it the metric keeps the server's label and the cell stays empty,
 * which is the honest answer — nobody knows.
 *
 * The durable fix is a flag on the request opting into the New label, which
 * would put the decision back with the side that owns impact classification
 * and can see its own snapshot coverage.
 */
export const resolveImpact = (
  metric: MetricDelta,
  comparison: RowComparison,
  window: ComparisonWindow
): CompareImpact => {
  if (
    !comparison.existed &&
    (metric.current ?? 0) > 0 &&
    startedWithinPeriod(
      window.rowStart,
      window.periodStart,
      window.periodEnd,
      window.allowFutureStart
    )
  ) {
    return "new";
  }
  return metric.impact;
};

/**
 * Whether a terms-currency delta is meaningful for this row.
 *
 * The two sides of the subtraction are each denominated in the contract's own
 * currency at their own point in time. If the contract was re-signed in another
 * currency, subtracting them produces a number in no currency at all, so the
 * cell is withheld rather than shown.
 */
export const spansTwoCurrencies = (comparison: RowComparison): boolean =>
  !!comparison.previousCurrency &&
  !!comparison.currency &&
  comparison.previousCurrency !== comparison.currency;
